/**
 * HamburgerMenu - Full-screen navigation drawer (Stitch design)
 *
 * Left-aligned drawer with rounded right edge, scrim overlay,
 * profile section, and navigation items.
 */

import { useAuth } from '@/hooks/useAuth'
import type { ViewId } from '@/hooks/useNavigation'

interface HamburgerMenuProps {
  isOpen: boolean
  currentView: ViewId
  onNavigate: (view: ViewId) => void
  onClose: () => void
}

const MENU_ITEMS: { view: ViewId; icon: string; label: string }[] = [
  { view: 'generate', icon: 'auto_awesome', label: 'Generate Cards' },
  { view: 'review', icon: 'style', label: 'Review Session' },
  { view: 'library', icon: 'library_books', label: 'Card Library' },
  { view: 'stats', icon: 'bar_chart', label: 'Statistics' },
]

export function HamburgerMenu({ isOpen, currentView, onNavigate, onClose }: HamburgerMenuProps) {
  const { user, isAuthenticated, logout } = useAuth()

  if (!isOpen) return null

  const handleLogout = async () => {
    await logout()
    onClose()
  }

  const displayName = user?.name || user?.email?.split('@')[0] || 'Language Learner'
  const initial = displayName.charAt(0).toUpperCase()

  return (
    <>
      {/* Scrim overlay */}
      <div
        className="fixed inset-0 z-[59] bg-black/40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <nav className="hamburger-menu fixed left-0 top-0 z-[60] h-full w-72 bg-surface-container-lowest rounded-r-[3rem] shadow-2xl flex flex-col">
        {/* Header with close button */}
        <div className="flex items-center justify-between p-6 pb-4">
          <h2 className="font-headline font-extrabold text-lg text-primary">Piccard</h2>
          <button
            className="p-2 rounded-xl hover:bg-surface-container-low transition-colors"
            onClick={onClose}
            aria-label="Close menu"
          >
            <span className="material-symbols-outlined text-on-surface">close</span>
          </button>
        </div>

        {/* Profile section */}
        <div className="px-6 py-4">
          <div className="flex items-center gap-3">
            {isAuthenticated && user?.picture ? (
              <img
                src={user.picture}
                alt={displayName}
                className="w-10 h-10 rounded-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary-container flex items-center justify-center">
                <span className="font-bold text-sm text-on-primary-container">
                  {isAuthenticated ? initial : '?'}
                </span>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm text-on-surface truncate">{displayName}</p>
              {isAuthenticated && user?.email ? (
                <p className="text-xs text-on-surface-variant truncate">{user.email}</p>
              ) : (
                <p className="text-xs text-on-surface-variant">Not signed in</p>
              )}
            </div>
          </div>
        </div>

        {/* Navigation items */}
        <div className="flex-1 py-4 px-3">
          {MENU_ITEMS.map(({ view, icon, label }) => (
            <button
              key={view}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left font-semibold text-sm transition-colors ${
                currentView === view
                  ? 'bg-primary text-on-primary'
                  : 'text-on-surface-variant hover:bg-surface-container-low'
              }`}
              onClick={() => onNavigate(view)}
            >
              <span className="material-symbols-outlined">{icon}</span>
              <span>{label}</span>
            </button>
          ))}

          {/* Divider */}
          <div className="my-3 mx-4 border-t border-outline-variant" />

          {/* Settings */}
          <button
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left font-semibold text-sm transition-colors ${
              currentView === 'settings'
                ? 'bg-primary text-on-primary'
                : 'text-on-surface-variant hover:bg-surface-container-low'
            }`}
            onClick={() => onNavigate('settings')}
          >
            <span className="material-symbols-outlined">settings</span>
            <span>Settings</span>
          </button>
        </div>

        {/* Footer */}
        <div className="p-6 pt-4">
          {isAuthenticated ? (
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left font-semibold text-sm text-error hover:bg-error-container/20 transition-colors"
            >
              <span className="material-symbols-outlined">logout</span>
              <span>Sign Out</span>
            </button>
          ) : (
            <p className="text-xs text-on-surface-variant text-center">v2.0 - New UI</p>
          )}
        </div>
      </nav>
    </>
  )
}
